/*
  RK product-card video (2026-09-10).

  Two behaviours, because the two devices have nothing in common here.

  DESKTOP (fine pointer): plays on hover, the way it always did. Loading is deferred to the first
  hover (`preload="none"` in the markup), so a collection page costs exactly as much as it did
  before until someone shows intent.

  MOBILE (coarse pointer): there is no hover to leave, so Henry asked for "somekind of an idea to
  make it interesting". The card plays when it reaches the MIDDLE of the screen and stops when it
  leaves — the flavour you are looking at is the one that moves. Exactly ONE video plays at a
  time: a grid of six autoplaying videos is a data bill and a hot phone, and it also destroys the
  effect, because nothing stands out when everything moves.

  The "middle of the screen" is done with one IntersectionObserver whose root is shrunk to the
  central band, so there is no scroll handler on the page at all. Candidates are measured only
  when the band's membership changes, and only the two or three cards inside it.

  Three ways to opt out, all silent, all leaving the still image in place: prefers-reduced-motion,
  Data Saver, and a 2g-class connection.
*/
(function () {
  var FINE_POINTER = window.matchMedia('(hover: hover) and (pointer: fine)');
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  function metered() {
    if (!conn) return false;
    if (conn.saveData) return true;
    return /^(slow-2g|2g)$/.test(conn.effectiveType || '');
  }

  function enabled() {
    return !REDUCED.matches && !metered();
  }

  function start(video) {
    if (!enabled() || video.dataset.rkOn === '1') return;
    var card = video.closest('.card-wrapper');
    if (!card) return;
    video.dataset.rkOn = '1';
    card.classList.add('rk-card--video-on');
    // play() rejects when the browser declines — Low Power Mode, no user gesture yet, data saver.
    // That is a normal outcome, not an error: fall back to the poster and clear the class.
    var p = video.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function () {
        video.dataset.rkOn = '0';
        card.classList.remove('rk-card--video-on');
      });
    }
  }

  function stop(video) {
    if (video.dataset.rkOn !== '1') return;
    var card = video.closest('.card-wrapper');
    video.dataset.rkOn = '0';
    if (card) card.classList.remove('rk-card--video-on');
    video.pause();
    // Rewind so the next time starts at the beginning rather than mid-pour.
    try { video.currentTime = 0; } catch (e) { /* not seekable yet */ }
  }

  /* ── Desktop: hover and keyboard focus ─────────────────────────────────── */

  function bindHover(video) {
    var card = video.closest('.card-wrapper');
    if (!card) return;
    card.addEventListener('pointerenter', function () { start(video); });
    card.addEventListener('pointerleave', function () { stop(video); });
    // Keyboard users reach the card by focusing its link; give them the same thing.
    card.addEventListener('focusin', function () { start(video); });
    card.addEventListener('focusout', function (e) {
      if (!card.contains(e.relatedTarget)) stop(video);
    });
  }

  /* ── Mobile: whichever card is nearest the centre of the screen ─────────── */

  var inBand = [];
  var current = null;
  var frame = 0;

  // -42% top and bottom leaves a 16%-tall band across the middle of the viewport. A card enters
  // the observer only while it overlaps that band, which keeps the candidate list at two or three.
  var bandObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var i = inBand.indexOf(e.target);
          if (e.isIntersecting && i === -1) inBand.push(e.target);
          else if (!e.isIntersecting && i !== -1) inBand.splice(i, 1);
        });
        schedule();
      }, { rootMargin: '-42% 0px -42% 0px', threshold: 0 })
    : null;

  function schedule() {
    if (frame) return;
    frame = requestAnimationFrame(pick);
  }

  function pick() {
    frame = 0;
    var middle = window.innerHeight / 2;
    var best = null;
    var bestDistance = Infinity;

    for (var i = 0; i < inBand.length; i++) {
      var r = inBand[i].getBoundingClientRect();
      var d = Math.abs((r.top + r.bottom) / 2 - middle);
      if (d < bestDistance) { bestDistance = d; best = inBand[i]; }
    }

    if (best === current) return;
    if (current) stop(current);
    current = best;
    if (current) start(current);
  }

  function bindViewport(video) {
    if (bandObserver) bandObserver.observe(video);
  }

  /* ── Wiring ────────────────────────────────────────────────────────────── */

  function bind(video) {
    if (video.dataset.rkBound === '1') return;
    video.dataset.rkBound = '1';
    if (FINE_POINTER.matches) bindHover(video);
    else bindViewport(video);
  }

  function scan(root) {
    (root || document).querySelectorAll('video.rk-card-video').forEach(bind);
  }

  // A backgrounded tab should not keep decoding video.
  document.addEventListener('visibilitychange', function () {
    if (document.hidden && current) { stop(current); current = null; }
    else schedule();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  // Facet filtering and infinite scroll swap the grid out from under us; rebind what appears and
  // drop anything that left, so a removed card cannot stay in the band list as the current video.
  var mo = new MutationObserver(function (records) {
    records.forEach(function (r) {
      r.addedNodes.forEach(function (n) {
        if (n.nodeType !== 1) return;
        if (n.matches && n.matches('video.rk-card-video')) bind(n);
        else if (n.querySelectorAll) scan(n);
      });
      r.removedNodes.forEach(function (n) {
        if (n.nodeType !== 1) return;
        inBand = inBand.filter(function (v) { return document.contains(v); });
        if (current && !document.contains(current)) { current = null; schedule(); }
      });
    });
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();
